import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { BookableService, ServiceBlackout, BookingSummary, BookingPricingMode } from '@evo-hub/shared';

export interface UpsertServiceInput {
  pricingMode: BookingPricingMode;
  /** Varios días (estadía) o uno solo (pase de día). */
  multiDay?: boolean;
  basePrice: number;
  pricePerPerson?: number | null;
  minNights?: number | null;
  maxNights?: number | null;
  minPeople?: number;
  maxPeople?: number | null;
  capacityPerDate?: number;
  weeklyAvailability?: number[];
}

export function useFormService(formId: string | undefined) {
  return useQuery<{ service: BookableService | null }>({
    queryKey: ['forms', formId, 'service'],
    queryFn: () => api.get<{ service: BookableService | null }>(`/api/forms/${formId}/service`),
    enabled: !!formId,
  });
}

export function useUpsertFormService(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertServiceInput) => api.put<BookableService>(`/api/forms/${formId}/service`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'service'] }),
  });
}

export function useRemoveFormService(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/api/forms/${formId}/service`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'service'] }),
  });
}

export function useServiceBlackouts(formId: string, enabled: boolean) {
  return useQuery<{ blackouts: ServiceBlackout[] }>({
    queryKey: ['forms', formId, 'blackouts'],
    queryFn: () => api.get<{ blackouts: ServiceBlackout[] }>(`/api/forms/${formId}/service/blackouts`),
    enabled,
  });
}

export function useAddBlackout(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { dateStart: string; dateEnd: string; reason?: string }) =>
      api.post<ServiceBlackout>(`/api/forms/${formId}/service/blackouts`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'blackouts'] }),
  });
}

export function useRemoveBlackout(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blackoutId: number) => api.delete<{ ok: boolean }>(`/api/forms/${formId}/service/blackouts/${blackoutId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'blackouts'] }),
  });
}

export function useServiceBookings(formId: string, enabled: boolean) {
  return useQuery<{ bookings: BookingSummary[] }>({
    queryKey: ['forms', formId, 'bookings'],
    queryFn: () => api.get<{ bookings: BookingSummary[] }>(`/api/forms/${formId}/service/bookings`),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCancelBooking(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => api.post<{ ok: boolean }>(`/api/forms/${formId}/service/bookings/${bookingId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms', formId, 'bookings'] }),
  });
}
